import { KeyboardReturnOutlined, ShortcutOutlined } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  Chip,
  Container,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import Screen from "../../components/layout/Screen";
import ScreenContent from "../../components/layout/ScreenContent";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { isArray, isNil } from "lodash";
import JsonPreview from "../../components/common/JsonPreview";
import Flx from "../../components/layout/Flx";
import TabPanel from "../../components/navigation/TabPanel";
import ToggleTabSwitcher from "../../components/navigation/ToggleTabSwitcher";
import Txt from "../../components/typography/Txt";
import { loportalRoutesConfig } from "./routeConfig/loportalRoutesConfig";
import { underwritingRoutesConfig } from "./routeConfig/underwritingRoutesConfig";
import { userRoutesConfig } from "./routeConfig/userRoutesConfig";
import { borrowerSubmissionEnpointsConfig } from "./routeConfig/borrowerSubmissionEnpointsConfig";

const EndpointListRoute = () => {
  const [payload, setPayload] = useState("{}");
  const [headers, setHeaders] = useState("{}");

  const [tab, setTab] = useState("All");

  const formatJson = (jsonString) => {
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return jsonString;
    }
  };

  const handleFormatPayload = () => {
    setPayload(formatJson(payload));
  };

  const handleFormatHeaders = () => {
    setHeaders(formatJson(headers));
  };

  // // Example payloads for quick testing
  // if (!user) {
  //   return (
  //     <Flx center sx={{ p: 5 }}>
  //       <LoadingFallback />
  //     </Flx>
  //   );
  // }

  // if (user && !isDevUser) {
  //   return <Navigate to="/" replace />;
  // }

  return (
    <Screen>
      <ScreenContent>
        <Container maxWidth="lg">
          <Typography variant="h4" gutterBottom sx={{ my: 2, fontWeight: 600 }}>
            API Endpoints By Route
          </Typography>

          <ToggleTabSwitcher
            value={tab}
            onChange={setTab}
            tabs={[
              "All",
              "Users",
              "Underwriting",
              "LO Portal",
              "Borrower Submissions",
            ]}
          />

          <TabPanel value={tab} tabValue="All">
            <EndpointDocumentation
              endpoints={[
                ...userRoutesConfig,
                ...loportalRoutesConfig,
                ...underwritingRoutesConfig,
                ...borrowerSubmissionEnpointsConfig,
              ]}
            />
          </TabPanel>
          <TabPanel value={tab} tabValue="Users">
            <EndpointDocumentation endpoints={userRoutesConfig} />
          </TabPanel>
          <TabPanel value={tab} tabValue="LO Portal">
            <EndpointDocumentation endpoints={loportalRoutesConfig} />
          </TabPanel>
          <TabPanel value={tab} tabValue="Underwriting">
            <EndpointDocumentation endpoints={underwritingRoutesConfig} />
          </TabPanel>
          <TabPanel value={tab} tabValue="Borrower Submissions">
            <EndpointDocumentation
              endpoints={borrowerSubmissionEnpointsConfig}
            />
          </TabPanel>
        </Container>
      </ScreenContent>
    </Screen>
  );
};

const EndpointDocumentation = ({ endpoints }) => {
  return (
    <Flx column gap={2} sx={{ py: 4 }}>
      {endpoints.map((endpoint, index) => (
        <EndpointCard key={index} index={index} endpoint={endpoint} />
      ))}
    </Flx>
  );
};

const EndpointCard = ({ endpoint, index, expandAll }) => {
  const [expanded, setExpanded] = useState(true);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const getMethodColor = (method) => {
    const colors = {
      GET: "primary",
      POST: "success",
      PUT: "warning",
      PATCH: "info",
      DELETE: "error",
    };
    return colors[method] || "default";
  };

  console.log("Rendering EndpointCard for:", endpoint.path);
  return (
    <Card sx={{ p: 0, mb: 0 }}>
      <Accordion
        expanded={expanded === `panel${index}`}
        onChange={handleChange(`panel${index}`)}
        sx={{ boxShadow: "none" }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            "&:hover": { backgroundColor: "action.hover" },
          }}
        >
          <Flx column gap={1}>
            <Typography variant="h6" sx={{ fontWeight: 400 }}>
              {endpoint.title}
            </Typography>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ width: "100%" }}
            >
              <Chip
                label={endpoint.method}
                color={getMethodColor(endpoint.method)}
                size="small"
                sx={{ fontWeight: 600, minWidth: 70, borderRadius: "6px" }}
              />
              <Txt bold>{endpoint.path}</Txt>
            </Stack>
          </Flx>
        </AccordionSummary>

        <AccordionDetails sx={{ borderTop: "1px solid #eee", pt: 3 }}>
          <Flx column gap={4} spacing={3}>
            <LowlightBox title="Description">
              {endpoint.description}
            </LowlightBox>
            <LowlightBox title="Purpose / Usage Context / Things to Know">
              {endpoint.purpose}
            </LowlightBox>
            <LowlightBox dividerTop title="Path">
              {endpoint.path}
            </LowlightBox>

            <LowlightBox title="Path Example">
              {endpoint.examplePath}
            </LowlightBox>

            {endpoint.requiredParams && endpoint.requiredParams.length > 0 && (
              <LowlightBox title="Required Parameters" outline>
                <Stack spacing={1}>
                  {endpoint.requiredParams.map((param, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        p: 1,
                        flexGrow: 0,
                        background: "#f9fafe",
                        border: "1px solid #e0e4fb",
                        borderRadius: "4px",
                      }}
                    >
                      <Typography variant="body2">
                        <strong>{param.name}</strong> ({param.type})
                        {param.location && (
                          <Chip
                            label={param.location}
                            size="small"
                            sx={{ ml: 1, height: 20 }}
                          />
                        )}
                      </Typography>
                      {param.description && (
                        <Typography variant="caption" color="text.secondary">
                          {param.description}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              </LowlightBox>
            )}
            {/* <Divider sx={{ my: 0.5 }} /> */}

            <LowlightBox
              dividerTop
              icon={<ShortcutOutlined fontSize="small" color="action" />}
              title="Request Payload"
              code
            >
              {endpoint.requestPayload}
            </LowlightBox>

            <LowlightBox
              icon={<KeyboardReturnOutlined fontSize="small" color="action" />}
              title="Response"
              code
            >
              {endpoint.requestResponse}
            </LowlightBox>
          </Flx>
        </AccordionDetails>
      </Accordion>
    </Card>
  );
};

const LowlightBox = ({
  title,
  children,
  dividerTop = false,
  dividerBottom = false,
  icon,
  outline = false,
  code = false,
}) => {
  if (isNil(children)) return null;
  return (
    <>
      {dividerTop && <Divider sx={{ my: 0.5 }} />}
      <Flx column sx={{ flexGrow: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          {icon}
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
        </Stack>

        {code ? (
          <CodeBox>{children}</CodeBox>
        ) : (
          <Box
            sx={{
              p: 1,
              background: outline ? "#fdfdfdff" : "#f9fafe",
              border: outline ? "1px solid #efeff1ff" : "1px solid #e0e4fb",
              borderRadius: "4px",
            }}
          >
            {isArray(children) ? (
              <Flx column gap={2}>
                {children.map((child, index) => (
                  <code key={index}>{child}</code>
                ))}
              </Flx>
            ) : (
              <code>{children}</code>
            )}
          </Box>
        )}
      </Flx>
      {dividerBottom && <Divider sx={{ my: 0.5 }} />}
    </>
  );
};

const CodeBox = ({ children }) => {
  if (isNil(children)) return null;

  console.log("CODE BOX", children);
  return (
    <Box
      sx={{
        p: 1,
        background: "#f9fafe",
        border: "1px solid #e0e4fb",
        borderRadius: "4px",
        maxHeight: "800px",
        overflow: "auto",
        ".json-view--header": {
          position: "sticky",
          top: 0,
          background: "#f9fafe",
          zIndex: 111,
        },
        ".json-view": {
          "span, div": {
            fontSize: "10px",
          },
        },
      }}
    >
      <JsonPreview defaultCollapse={2} values={children} />
    </Box>
  );
};

const OutlineBox = ({ children }) => {
  if (isNil(children)) return null;
  return (
    <Box
      sx={{
        p: 1,
        background: "#fdfdfdff",
        border: "1px solid #efeff1ff",
        borderRadius: "4px",
      }}
    >
      <JsonPreview values={children} />
    </Box>
  );
};

export default EndpointListRoute;
